import React, { useState } from 'react';

import Button from 'CommonComponents/Button';
import DatePickerField from 'CommonComponents/DatePickerField';
import FormInput from 'CommonComponents/FormInput';
import SelectInput from 'CommonComponents/SelectInput';

const TYPE_LABELS = {
    Permanent: () => $t('Permanent'),
    New: () => $t('New'),
    Temporary: () => $t('Temporary'),
};

/**
 * A venue by hand: name, code, type and hire period. The hire period starts
 * as the Key dates event window. onSubmit returns a promise; validation errors
 * are shown against their fields.
 */
export default function AddVenueForm({ venueTypes, defaultHire, onSubmit, onCancel }) {
    const [values, setValues] = useState({
        name: '',
        code: '',
        type: venueTypes[0] ?? '',
        hire_start: defaultHire.start ?? '',
        hire_end: defaultHire.end ?? '',
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const set = (field, value) => {
        setErrors((current) => ({ ...current, [field]: undefined }));
        setValues((current) => ({ ...current, [field]: value }));
    };

    const submit = (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErrors({});

        onSubmit({ ...values, name: values.name.trim(), code: values.code.trim() })
            .catch((err) => {
                const fieldErrors = err?.response?.data?.errors;
                if (fieldErrors) {
                    setErrors(Object.fromEntries(Object.entries(fieldErrors).map(([field, messages]) => [field, [].concat(messages).join(' ')])));
                }
            })
            .finally(() => setSubmitting(false));
    };

    const dateField = (field, label) => (
        <div className="tw-flex tw-flex-col tw-space-y-1.5">
            <label htmlFor={`setup-wizard-venue-${field}`} className="text-input__label">
                {label}
            </label>
            <DatePickerField
                id={`setup-wizard-venue-${field}`}
                name={field}
                valueFormat="yyyy-MM-dd"
                className={errors[field] ? 'tw-border tw-border-red-500 text-input' : 'tw-border-0 text-input'}
                value={values[field]}
                disabled={submitting}
                onChange={set}
            />
            {errors[field] && <div className="tw-text-red-500 tw-text-sm">{errors[field]}</div>}
        </div>
    );

    return (
        <form
            className="tw-flex tw-flex-col tw-gap-y-3 tw-w-full tw-px-4 tw-py-3 tw-rounded-lg tw-border tw-border-charcoal-15 tw-bg-background"
            onSubmit={submit}
        >
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
                <FormInput
                    id="setup-wizard-venue-name"
                    name="setup-wizard-venue-name"
                    label={$t('Venue name')}
                    maxLength={191}
                    autoFocus
                    value={values.name}
                    readOnly={submitting}
                    showError={!!errors.name}
                    errorText={errors.name}
                    onChange={(e) => set('name', e.target.value)}
                />
                <FormInput
                    id="setup-wizard-venue-code"
                    name="setup-wizard-venue-code"
                    label={$t('Venue code')}
                    maxLength={10}
                    value={values.code}
                    readOnly={submitting}
                    showError={!!errors.code}
                    errorText={errors.code}
                    onChange={(e) => set('code', e.target.value)}
                />
                <SelectInput
                    id="setup-wizard-venue-type"
                    name="setup-wizard-venue-type"
                    label={$t('Venue type')}
                    value={values.type}
                    disabled={submitting}
                    showError={!!errors.type}
                    errorText={errors.type}
                    onChange={(e) => set('type', e.target.value)}
                >
                    {venueTypes.map((type) => (
                        <option key={type} value={type}>
                            {TYPE_LABELS[type]?.() ?? type}
                        </option>
                    ))}
                </SelectInput>
                <div className="tw-hidden sm:tw-block" />
                {dateField('hire_start', $t('Hire start date'))}
                {dateField('hire_end', $t('Hire end date'))}
            </div>
            <div className="tw-flex tw-gap-2">
                <Button type="submit" variant="primary" small loading={submitting} disabled={submitting}>
                    {$t('Save')}
                </Button>
                <Button variant="outline" small disabled={submitting} onClick={onCancel}>
                    {$t('Cancel')}
                </Button>
            </div>
        </form>
    );
}
